import React, { Fragment } from 'react'

import PropTypes from 'prop-types'
import styled from 'styled-components'
import { twMerge } from 'tailwind-merge'

import { Row } from '../Grid'
import { ConsultationChecked } from '../Icons/assets'
import { Text } from '../Text'

const Radio = ({
	initial,
	checked,
	disabled,
	selected,
	icon,
	name,
	value,
	label,
	register,
	onClick,
	endIcon,
	radioId,
	hiddenInputId,
	variant,
	numberEvents,
	defaultChecked,
	onChange,
	className,
	...props
}) => (
	<Fragment>
		{variant === 'primary' && (
			<Row
				data-testid='radio-id'
				as='label'
				className={twMerge('relative items-center bg-transparent', className)}
				selected={selected}
				htmlFor={`${name}${value}`}
				onClick={onClick}
				{...props}
			>
				<HiddenInput
					data-testid={hiddenInputId || 'hidden-input-id'}
					disabled={disabled}
					name={name}
					id={`${name}${value}`}
					type='radio'
					value={value}
					ref={register}
					{...(checked && { checked: checked })}
					{...(defaultChecked && { defaultChecked: defaultChecked })}
					{...(onChange && { onChange: onChange })}
				/>

				<Row
					className={twMerge(
						'relative mr-[10px] h-4 w-4 min-w-4 items-center justify-center rounded-full border border-solid',
						disabled
							? 'border-gray-900 bg-white'
							: 'border-[#CAD0D6] bg-transparent'
					)}
				>
					<div />
				</Row>

				<Row className={twMerge('items-center', endIcon && 'flex-row-reverse')}>
					{icon}
					<Text
						as='span'
						className={twMerge(
							'mr-2 text-base font-normal',
							icon ? 'ml-1.5' : 'ml-0',
							disabled ? 'text-gray-900' : 'text-gray-800'
						)}
					>
						{label}
					</Text>
				</Row>
			</Row>
		)}
		{variant === 'secondary' && (
			<Row
				as='label'
				className={twMerge('relative', className)}
				htmlFor={name}
				onClick={onClick}
				{...props}
			>
				<SquareRadio
					name={name}
					type='radio'
					value={value}
					ref={register}
					disabled={disabled}
					variant={variant}
					checked={checked}
					defaultChecked={defaultChecked}
				/>
				<Row
					className={twMerge(
						'w-full items-center justify-center rounded py-1.5',
						disabled ? 'bg-white' : 'bg-gray-900'
					)}
				>
					<Text
						variant='tiny'
						className={twMerge(
							'font-bold md:text-sm',
							disabled ? 'text-gray-900' : 'text-green-300'
						)}
					>
						{label}
					</Text>
				</Row>
			</Row>
		)}
		{variant === 'tertiary' && (
			<Row
				data-testid='radio-id'
				as='label'
				className={twMerge('relative items-center bg-transparent', className)}
				selected={selected}
				htmlFor={name}
				onClick={onClick}
				{...props}
			>
				<HiddenInput
					data-testid={hiddenInputId || 'hidden-input-id'}
					variant={variant}
					disabled={disabled}
					name={name}
					type='radio'
					value={value}
					ref={register}
					checked={checked}
					defaultChecked={defaultChecked}
				/>
				<Row
					className={twMerge(
						'relative h-[23px] w-[23px] min-w-4 items-center justify-center rounded-full border border-solid',
						label ? 'mr-[10px]' : 'mr-0',
						disabled
							? 'border-gray-900 bg-white'
							: checked
								? 'border-transparent bg-transparent'
								: 'border-[#b8b8b8] bg-transparent'
					)}
				>
					<Row>
						<ConsultationChecked color='white' width='12px' height='12px' />
					</Row>
				</Row>

				<Row className={twMerge('items-center', endIcon && 'flex-row-reverse')}>
					{icon}
					<Text
						as='span'
						variant='small'
						className={twMerge(
							'mr-2',
							icon ? 'ml-1.5' : 'ml-0',
							disabled ? 'text-gray-900' : 'text-gray-800'
						)}
					>
						{label}
					</Text>
				</Row>
			</Row>
		)}
		{variant === 'quarternary' && (
			<Row
				as='label'
				className={twMerge('relative', className)}
				data-testid='radio-id'
				htmlFor={name}
				onClick={onClick}
				{...props}
			>
				<SquareRadio
					name={name}
					type='radio'
					value={value}
					ref={register}
					disabled={disabled}
					variant={variant}
					checked={checked}
					defaultChecked={defaultChecked}
				/>
				<Row
					className={twMerge(
						'relative w-full items-center justify-center rounded py-1.5',
						disabled ? 'bg-white' : 'bg-yellow-700'
					)}
				>
					<Text
						variant='tiny'
						className={twMerge(
							'font-normal md:text-sm',
							disabled ? 'text-gray-900' : 'text-white'
						)}
					>
						{label}
					</Text>

					<InfoEvents data-testid='InfoEvents-id'>{numberEvents}</InfoEvents>
				</Row>
			</Row>
		)}
	</Fragment>
)

Radio.defaultProps = {
	selected: false,
	disabled: false,
	checked: false,
	endIcon: false,
	variant: 'primary',
	defaultChecked: false
}

Radio.propTypes = {
	selected: PropTypes.bool,
	disabled: PropTypes.bool,
	name: PropTypes.string,
	value: PropTypes.number,
	label: PropTypes.string,
	onClick: PropTypes.func,
	endIcon: PropTypes.bool,
	variant: PropTypes.string,
	checked: PropTypes.bool,
	defaultChecked: PropTypes.bool
}

const HiddenInput = styled.input(
	({ theme: { colors }, disabled, variant }) => `

  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 1;
  z-index: 20;
  cursor: ${disabled ? 'default' : 'pointer'};
  opacity: 0;

   &:not(:checked) {
    left: 0 !important;
    + div {
      div {
        transition: ${variant === 'tertiary' ? 'none' : 'all linear 100ms'};
        opacity: 0;
      }
    }
  }

  &:checked {
    + div {
      div {
        padding: ${variant === 'tertiary' ? 'none' : '3px'};
        border-radius: 50%;
        border: 5px solid ${colors.primary};
        background-color: ${variant === 'tertiary' ? colors.orange : 'transparent'};
      }
    }
  }
`
)

const SquareRadio = styled.input(
	({ theme: { colors }, disabled, variant }) => `

  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 1;
  z-index: 20;
  cursor: ${disabled ? 'not-allowed' : 'pointer'};
  opacity: 0;

  &:not(:checked) {
    left: 0 !important;
  }

  &:checked ~ div {
    cursor: pointer;
    transition: all linear 100ms;
    background: ${variant === 'quarternary' ? colors.yellow : colors.lightGreen};
    p {
      color: ${colors.white};
    }
  }

`
)

const InfoEvents = styled(Text)`
	background: #ff7708;
	position: absolute;
	border-radius: 50%;
	height: 22px;
	width: 22px;
	top: -6px;
	right: -6px;
	display: flex;
	justify-content: center;
	align-items: center;
	color: #ffffff;
	font-size: 12px;
	line-height: normal;
`

export { Radio }
